import React from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  CaretRight,
  ChatText,
  Copy,
  Cpu,
  Envelope,
  Lightning,
  Microphone,
  Star,
  UsersThree
} from '@phosphor-icons/react';

/**
 * TemplateCard - Enhanced card for displaying agent templates
 * 
 * Shows:
 * - Name and description
 * - Category/type
 * - Popularity/usage stats
 * - Key capabilities
 */
const TemplateCard = ({ template, onClick, index = 0 }) => {
  const { 
    name, 
    description, 
    category,
    capability_profile,
    use_count = 0,
    rating,
    key_capabilities = [],
    tags = []
  } = template;

  const categoryIcons = {
    'customer-support': ChatText,
    'booking': Calendar,
    'voice': Microphone,
    'automation': Lightning,
    'notification': Envelope,
    'default': Cpu
  };

  const CategoryIcon = categoryIcons[category] || categoryIcons.default;

  const categoryColors = {
    'customer-support': 'text-blue-400 bg-blue-500/10',
    'booking': 'text-purple-400 bg-purple-500/10',
    'voice': 'text-pink-400 bg-pink-500/10',
    'automation': 'text-amber-400 bg-amber-500/10',
    'notification': 'text-cyan-400 bg-cyan-500/10',
    'default': 'text-slate-400 bg-slate-500/10'
  };

  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ scale: 1.02, x: 4 }}
      onClick={onClick}
      className="w-full p-4 rounded-xl bg-gradient-to-br from-amber-500/5 to-orange-500/5 
        border border-amber-500/20 hover:border-amber-500/40 
        text-left transition-all group"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="p-2 rounded-lg bg-amber-500/10 flex-shrink-0">
          <Copy className="w-4 h-4 text-amber-400" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-white text-sm truncate group-hover:text-amber-400 transition-colors">
              {name}
            </h4>
            <CaretRight className="w-4 h-4 text-slate-500 group-hover:text-amber-400 
              group-hover:translate-x-1 transition-all flex-shrink-0" />
          </div>
          
          <p className="text-xs text-slate-500 line-clamp-2 mb-2">
            {description}
          </p>

          {/* Meta Info */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Category Badge */}
            {category && (
              <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${categoryColors[category] || categoryColors.default}`}>
                <CategoryIcon className="w-3 h-3" />
                {category.replace(/-/g, ' ')}
              </span>
            )}

            {/* Usage Count */}
            {use_count > 0 && (
              <span className="flex items-center gap-1 text-[10px] text-slate-500">
                <UsersThree className="w-3 h-3" />
                {use_count.toLocaleString()} uses
              </span>
            )}

            {/* Rating */}
            {rating && (
              <span className="flex items-center gap-1 text-[10px] text-amber-400">
                <Star className="w-3 h-3 fill-current" />
                {rating.toFixed(1)}
              </span>
            )}
          </div>

          {/* Tags/Capabilities */}
          {(key_capabilities.length > 0 || tags.length > 0) && (
            <div className="flex items-center gap-1 mt-2 flex-wrap">
              {[...key_capabilities, ...tags].slice(0, 3).map((item, idx) => (
                <span 
                  key={idx}
                  className="px-1.5 py-0.5 rounded bg-white/5 text-[10px] text-slate-400"
                >
                  {item.replace(/_/g, ' ')}
                </span>
              ))}
              {[...key_capabilities, ...tags].length > 3 && (
                <span className="text-[10px] text-slate-500">
                  +{[...key_capabilities, ...tags].length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.button>
  );
};

export default TemplateCard;
